import {
    SpendGoalsWidget as SharedSpendGoalsWidget,
    type SpendGoalsWidgetComponentProps,
} from '@fruitful/agent-ui';
import { CONVERSATIONAL_MM_AGENT } from '@fruitful/llm-service-shared';
import { MoneyMapEmbeddedVisual } from '@fruitful/money-map';
import {
    buildInvestmentWaterfall,
    toPersistedInvestmentWaterfall,
} from '@fruitful/money-map-simulator';
import {
    type AhaMomentWidgetProps,
    AnalyticsUserEvent,
    CMMBranch,
    type OnboardingChatResults,
} from '@fruitful/types';
import { useFeatureGate } from '@statsig/expo-bindings';
import { router, useLocalSearchParams } from 'expo-router';
import { useAtomValue, useSetAtom } from 'jotai';
import React, { useCallback, useEffect } from 'react';
import {
    Button,
    ButtonSize,
    ButtonVariant,
    H6Regular,
    Shadows,
    XStack,
    YStack,
} from 'smoothie';

import { StorageKeys } from '../../common/constants';
import storage from '../../common/providers/StorageFacade';
import { useViewEvents } from '../../events/hooks/useViewEvents';
import { FeatureFlags } from '../../feature-flags/FeatureFlags';
import { track, trackGrowthMarketingEvent } from '../../logger/Logger';
import { useVisitorId } from '../../user/providers/VisitorProvider';
import { useUpsertMastraThread } from '../hooks/useUpsertMastraThread';
import {
    ahaMomentSheetTitleAtom,
    isBuildingPlanAtom,
    isChatFadingOutAtom,
    isChatInputLockedAtom,
    isPlanReadyAtom,
    moneyMapNumbersAtom,
    onboardingChatResultsAtom,
    welcomeSelectionAtom,
} from '../state/OnboardingChatAtoms';

/** Hold the transition message on screen before fading. */
const AUTO_NAVIGATE_HOLD_MS = 2000;
/** Slow fade-out duration before navigating to results. */
const AUTO_NAVIGATE_FADE_MS = 800;

/**
 * Threads that already scheduled auto-navigate timers. Survives AhaMoment
 * remounts so cleanup from an earlier mount cannot cancel navigation.
 */
const scheduledAutoNavigateThreadIds = new Set<string>();

export const clearScheduledCmmAutoNavigate = (
    threadId?: string | null,
): void => {
    if (threadId != null && threadId !== '') {
        scheduledAutoNavigateThreadIds.delete(threadId);
        return;
    }
    scheduledAutoNavigateThreadIds.clear();
};

export const AhaMomentWidget = ({
    intake,
    image,
    text,
    date,
    moneyMap,
    slides,
    highInterestDebtTotalCents,
    chatVersion,
    artifactVersion,
    autoNavigate,
}: AhaMomentWidgetProps) => {
    const { trackViewOnce } = useViewEvents();
    const setAhaMomentSheetTitle = useSetAtom(ahaMomentSheetTitleAtom);
    const setMoneyMapNumbers = useSetAtom(moneyMapNumbersAtom);
    const setOnboardingChatResults = useSetAtom(onboardingChatResultsAtom);
    const welcomeSelection = useAtomValue(welcomeSelectionAtom);
    const setIsChatInputLocked = useSetAtom(isChatInputLockedAtom);
    const setIsBuildingPlan = useSetAtom(isBuildingPlanAtom);
    const setIsChatFadingOut = useSetAtom(isChatFadingOutAtom);
    const setIsPlanReady = useSetAtom(isPlanReadyAtom);
    const { upsertMastraThread } = useUpsertMastraThread();
    const visitorId = useVisitorId();
    const params = useLocalSearchParams();

    const { value: useNewArtifact } = useFeatureGate(
        FeatureFlags.ENABLE_ARTIE_THE_ARTIFACT,
    );

    const navigate = useCallback(async () => {
        const threadId = storage.getString(
            StorageKeys.CONVERSATIONAL_MM_THREAD_ID,
        );

        const investmentWaterfall =
            intake.branch === CMMBranch.StartInvesting &&
            intake.investment != null
                ? toPersistedInvestmentWaterfall(
                      buildInvestmentWaterfall(intake.investment),
                  )
                : undefined;

        const results: OnboardingChatResults = {
            moneyMap: {
                incomeTotalCents: moneyMap.incomeTotalCents,
                billsTotalCents: moneyMap.billsTotalCents,
                spendTotalCents: moneyMap.spendTotalCents,
                goalsTotalCents: moneyMap.goalsTotalCents,
            },
            artifactData: {
                debtFreeDate: date,
                slides: slides.join(','),
                image,
                ...(useNewArtifact && intake.branch !== CMMBranch.StartInvesting
                    ? { version: 2 }
                    : {}),
            },
            rawChatResults: intake,
            ...(highInterestDebtTotalCents != null && {
                highInterestDebtTotalCents,
            }),
            ...(investmentWaterfall != null && { investmentWaterfall }),
            ...(chatVersion != null && { chatVersion }),
            ...(artifactVersion != null && { artifactVersion }),
            ...(welcomeSelection != null && { welcomeSelection }),
        };

        // Persist best-effort — never block the results redirect on upsert failure.
        if (threadId != null && threadId !== '') {
            try {
                await upsertMastraThread({
                    mastraThreadId: threadId,
                    agentId: CONVERSATIONAL_MM_AGENT.id,
                    visitorId,
                    version: '1.0',
                    results,
                });
            } catch (error) {
                track(
                    AnalyticsUserEvent.CMM_RESULTS_THREAD_SAVE_ERROR,
                    {
                        visitorId,
                        threadId,
                        errorMessage:
                            error instanceof Error ? error.message : 'unknown',
                    },
                    /* sendImmediately */ true,
                );
            }
        }

        setOnboardingChatResults(results);

        track(
            AnalyticsUserEvent.SIGNUP_FUNNEL_CMM_GO_TO_RESULTS,
            {
                autoNavigate: autoNavigate ?? false,
                visitorId,
                threadId: threadId ?? '',
            },
            /* sendImmediately */ true,
        );
        trackGrowthMarketingEvent(
            AnalyticsUserEvent.SIGNUP_FUNNEL_CMM_GO_TO_RESULTS,
            {
                visitorId,
                threadId: threadId ?? '',
            },
        );

        router.navigate({
            pathname: '/sign-up/get-started/results',
            params,
        });
    }, [
        date,
        image,
        moneyMap,
        slides,
        highInterestDebtTotalCents,
        autoNavigate,
        visitorId,
        intake,
        upsertMastraThread,
        setOnboardingChatResults,
        params,
        welcomeSelection,
    ]);

    useEffect(() => {
        setAhaMomentSheetTitle(`${text} ${date}`);
        if (moneyMap != null) {
            setMoneyMapNumbers(moneyMap);
        }
        if (autoNavigate !== true) {
            return undefined;
        }

        setIsChatInputLocked(true);
        setIsChatFadingOut(false);

        const threadId =
            storage.getString(StorageKeys.CONVERSATIONAL_MM_THREAD_ID) ??
            'unknown';
        if (scheduledAutoNavigateThreadIds.has(threadId)) {
            return undefined;
        }
        scheduledAutoNavigateThreadIds.add(threadId);

        // show-intake-summary is the single trigger for the plan transition
        // across all branches. Set isBuildingPlan now (shows the canned
        // message + typing indicator), then delay isPlanReady so the typing
        // animation has time to play out before navigating.
        setIsBuildingPlan(true);
        setIsChatInputLocked(true);
        const transitionDelay = 1500;

        // Intentionally not cleared on unmount — remounts were cancelling
        // navigate and leaving members stuck on "putting your plan together".
        setTimeout(() => {
            setIsPlanReady(true);
        }, transitionDelay);

        setTimeout(() => {
            setIsChatFadingOut(true);
        }, transitionDelay + AUTO_NAVIGATE_HOLD_MS);

        setTimeout(
            () => {
                void navigate();
            },
            transitionDelay + AUTO_NAVIGATE_HOLD_MS + AUTO_NAVIGATE_FADE_MS,
        );

        return undefined;
    }, []);

    if (autoNavigate === true) {
        return null;
    }

    trackViewOnce('SignUp/GetStarted-CMM-Chat/AhaMoment');

    return (
        <YStack
            gap={24}
            alignItems="center"
            justifyContent="center"
            width="100%"
        >
            <YStack
                alignSelf="center"
                width="100%"
                maxWidth={345}
                padding={24}
                backgroundColor="rgba(255, 255, 255, 0.5)"
                borderRadius={24}
                borderWidth={1}
                borderColor="$white"
                alignItems="center"
                gap={24}
                {...Shadows.SMALL}
            >
                <H6Regular>Your Money Map</H6Regular>
                <MoneyMapEmbeddedVisual
                    incomeTotalCents={moneyMap.incomeTotalCents}
                    billsTotalCents={moneyMap.billsTotalCents}
                    spendTotalCents={moneyMap.spendTotalCents}
                    goalsTotalCents={moneyMap.goalsTotalCents}
                />
            </YStack>
            <XStack
                width="100%"
                justifyContent="flex-end"
                alignContent="flex-end"
            >
                <Button
                    variant={ButtonVariant.PRIMARY}
                    size={ButtonSize.LARGE}
                    text="Looks good"
                    onPress={navigate}
                    maxWidth={150}
                />
            </XStack>
        </YStack>
    );
};

export const SpendGoalsWidget = ({
    onConfirm,
    ...props
}: SpendGoalsWidgetComponentProps) => {
    const { trackViewOnce } = useViewEvents();

    trackViewOnce('SignUp/GetStarted-CMM-Chat/SpendGoals');

    return (
        <SharedSpendGoalsWidget
            {...props}
            onSlideEnd={(monthlySpendCents) => {
                track(AnalyticsUserEvent.CMM_SPEND_GOALS_WIDGET_SLIDER, {
                    spendPercentage: monthlySpendCents / props.remainingCents,
                });
            }}
            onConfirm={
                onConfirm != null
                    ? (values) => {
                          track(AnalyticsUserEvent.CMM_SPEND_GOALS_CONFIRMED, {
                              ...values,
                          });
                          onConfirm(values);
                      }
                    : undefined
            }
        />
    );
};
